import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Config } from '../../config';
import Transition from '../../utils/Transition';
import LoadingSpinner from '../../components/LoadingSpinner';

function UserSidebar({
  userSidebarOpen,
  setUserSidebarOpen,
  selectedUser = null,
  onUserAdded = () => {}
}) {
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    // User table fields
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    is_active: true,
    role: 'user',
    
    // UserInfo table fields
    first_name: '',
    last_name: '',
    org: '',
    title: '',
    city: '',
    country: '',
    avatar: '',
    office: '',
    mobile: '',
  });
  
  const [errors, setErrors] = useState({});
  const [organizations, setOrganizations] = useState([]);
  const [countries, setCountries] = useState([]);
  
  // Countries data
  const COUNTRIES_DATA = [
    { code: 'AR', name: 'Argentina' },
    { code: 'BO', name: 'Bolivia' },
    { code: 'BR', name: 'Brazil' },
    { code: 'CL', name: 'Chile' },
    { code: 'CO', name: 'Colombia' },
    { code: 'CR', name: 'Costa Rica' },
    { code: 'CU', name: 'Cuba' },
    { code: 'DO', name: 'Dominican Republic' },
    { code: 'EC', name: 'Ecuador' },
    { code: 'SV', name: 'El Salvador' },
    { code: 'GT', name: 'Guatemala' },
    { code: 'HN', name: 'Honduras' },
    { code: 'MX', name: 'Mexico' },
    { code: 'NI', name: 'Nicaragua' },
    { code: 'PA', name: 'Panama' },
    { code: 'PY', name: 'Paraguay' },
    { code: 'PE', name: 'Peru' },
    { code: 'PR', name: 'Puerto Rico' },
    { code: 'UY', name: 'Uruguay' },
    { code: 'VE', name: 'Venezuela' },
    { code: 'US', name: 'United States' },
    { code: 'CA', name: 'Canada' },
    { code: 'ES', name: 'Spain' }
  ];
  
  // Load organizations and countries for dropdowns
  useEffect(() => {
    if (userSidebarOpen) {
      setCountries(COUNTRIES_DATA);
      
      const fetchOrganizations = async () => {
        try {
          const response = await axios.get(
            `${Config.APIBaseURL}/customers/`,
            getAxiosConfig()
          );
          setOrganizations(response.data);
        } catch (error) {
          console.error('Error fetching organizations:', error);
        }
      };
      
      fetchOrganizations();
    }
  }, [userSidebarOpen]);
  
  // Reset form when sidebar opens
  useEffect(() => {
    if (userSidebarOpen) {
      if (selectedUser) {
        // Edit mode - populate form with selected user
        setFormData({
          // User table fields
          username: selectedUser.username || '',
          email: selectedUser.email || '',
          password: '',
          confirmPassword: '',
          is_active: selectedUser.is_active ?? true,
          role: selectedUser.role || 'user',
          
          // UserInfo table fields
          first_name: selectedUser.user_info?.first_name || '',
          last_name: selectedUser.user_info?.last_name || '',
          org: selectedUser.user_info?.org || '',
          title: selectedUser.user_info?.title || '',
          city: selectedUser.user_info?.city || '',
          country: selectedUser.user_info?.country || '',
          avatar: selectedUser.user_info?.avatar || '',
          office: selectedUser.user_info?.office || '',
          mobile: selectedUser.user_info?.mobile || '',
        });
      } else {
        // Create mode - reset form
        setFormData({
          // User table fields
          username: '',
          email: '',
          password: '',
          confirmPassword: '',
          is_active: true,
          role: 'user',
          
          // UserInfo table fields
          first_name: '',
          last_name: '',
          org: '',
          title: '',
          city: '',
          country: '',
          avatar: '',
          office: '',
          mobile: '',
        });
      }
      setErrors({});
    }
  }, [userSidebarOpen, selectedUser]);
  
  const getAxiosConfig = () => ({
    headers: {
      'access_token': `Bearer ${localStorage.getItem('token')}`,
      'Content-Type': 'application/json'
    },
    withCredentials: true
  });
  
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };
  
  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Start loading state
    setIsLoading(true);
    
    // Load the image to be compressed
    const img = new Image();
    const reader = new FileReader();
    
    reader.onload = (event) => {
      img.onload = function() {
        // Create canvas for compression
        const canvas = document.createElement('canvas');
        
        // Calculate new dimensions (maintain aspect ratio)
        let width = img.width;
        let height = img.height;
        const maxDimension = 400; // Max dimension for avatar
        
        if (width > height && width > maxDimension) {
          height = Math.round((height * maxDimension) / width);
          width = maxDimension;
        } else if (height > maxDimension) {
          width = Math.round((width * maxDimension) / height);
          height = maxDimension;
        }
        
        // Set canvas dimensions
        canvas.width = width;
        canvas.height = height;
        
        // Draw image on canvas
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        
        // Start with high quality
        let quality = 0.9;
        let dataUrl = canvas.toDataURL('image/jpeg', quality);
        let iterations = 0;
        
        // Compress iteratively until size is below threshold or max iterations reached
        const compressImage = () => {
          // Calculate estimated size
          const base64Data = dataUrl.split(',')[1];
          const estimatedSize = Math.ceil(base64Data.length * 0.75);
          const maxSize = 250 * 1024; // 250KB
          
          if (estimatedSize <= maxSize || iterations >= 10) {
            // We're done compressing
            setFormData(prev => ({ ...prev, avatar: dataUrl }));
            
            // Clear avatar error if it exists
            if (errors.avatar) {
              setErrors(prev => {
                const newErrors = {...prev};
                delete newErrors.avatar;
                return newErrors;
              });
            }
            
            setIsLoading(false);
            return;
          }
          
          // Continue compressing
          iterations++;
          
          // Reduce quality for next iteration
          quality = Math.max(0.1, quality - 0.1);
          dataUrl = canvas.toDataURL('image/jpeg', quality);
          
          // Continue compression on next tick to avoid blocking UI
          setTimeout(compressImage, 0);
        };
        
        // Start compression
        compressImage();
      };
      
      img.onerror = function() {
        setErrors(prev => ({
          ...prev,
          avatar: 'Invalid image file'
        }));
        e.target.value = '';
        setIsLoading(false);
      };
      
      img.src = event.target.result;
    };
    
    reader.onerror = function() {
      setErrors(prev => ({
        ...prev,
        avatar: 'Error reading file'
      }));
      e.target.value = '';
      setIsLoading(false);
    };
    
    reader.readAsDataURL(file);
  };
  
  const validateForm = () => {
    const newErrors = {};
    
    // Username validation
    if (!formData.username) newErrors.username = 'Username is required';
    
    // Email validation
    if (!formData.email) newErrors.email = 'Email is required';
    
    // First/Last name validation
    if (!formData.first_name) newErrors.first_name = 'First name is required';
    if (!formData.last_name) newErrors.last_name = 'Last name is required';
    
    // Avatar validation - check size if it's a data URL
    if (formData.avatar && formData.avatar.startsWith('data:')) {
      // Estimate size: (data URL length - metadata) * 0.75 (base64 overhead)
      const base64Data = formData.avatar.split(',')[1];
      const estimatedSize = (base64Data.length * 0.75);
      const maxSize = 250 * 1024; // 250KB
      
      if (estimatedSize > maxSize) {
        newErrors.avatar = 'Image size must be less than 250KB';
      }
    }
    
    // Password validation only for new users or when changing password
    if (!selectedUser && !formData.password) {
      newErrors.password = 'Password is required';
    }
    
    if (formData.password && formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }
    
    if (formData.password && formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    
    // Organization validation
    if (!formData.org) newErrors.org = 'Organization is required';
    
    // Country validation
    if (!formData.country) newErrors.country = 'Country is required';
    
    return newErrors;
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    
    setIsLoading(true);
    
    try {
      // Match the backend endpoint's expected parameters structure
      // The backend expects two separate objects: newuser and userinfo
      const requestData = {
        // This maps to the newuser parameter (schemas.UserCreate)
        newuser: {
          username: formData.username,
          email: formData.email,
          role: formData.role,
          password: formData.password
        },
        
        // This maps to the userinfo parameter (schemas.UserInfoCreate)
        userinfo: {
          first_name: formData.first_name,
          last_name: formData.last_name,
          org: formData.org ? parseInt(formData.org, 10) : null,
          title: formData.title || '',
          city: formData.city || '',
          country: formData.country || '',
          avatar: formData.avatar || '',
          office: formData.office || '',
          mobile: formData.mobile || ''
        }
      };
      
      let response;
      if (selectedUser) {
        // For update, structure might be different
        response = await axios.put(
          `${Config.APIBaseURL}/user/update/${selectedUser.id}`,
          requestData,
          getAxiosConfig()
        );
      } else {
        // For create, use the structure we defined
        response = await axios.post(
          `${Config.APIBaseURL}/user/create`,
          requestData,
          getAxiosConfig()
        );
      }
      
      if (response.status === 200 || response.status === 201) {
        setUserSidebarOpen(false);
        onUserAdded(response.data);
      }
    } catch (error) {
      console.error('Error saving user:', error);
      
      if (error.response?.data?.detail) {
        setErrors({ general: typeof error.response.data.detail === 'string' 
          ? error.response.data.detail 
          : JSON.stringify(error.response.data.detail) 
        });
      } else if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
      } else {
        setErrors({ general: 'Error saving user. Please try again.' });
      }
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <Transition
      show={userSidebarOpen}
      tag="div"
      className="fixed inset-0 overflow-hidden z-50"
      enter="transition ease-in-out duration-200"
      enterStart="opacity-0"
      enterEnd="opacity-100"
      leave="transition ease-in-out duration-200"
      leaveStart="opacity-100"
      leaveEnd="opacity-0"
    >
      <div className="absolute inset-0 overflow-hidden">
        <Transition
          show={userSidebarOpen}
          tag="div"
          className="absolute inset-0 bg-slate-900 bg-opacity-30"
          enter="transition ease-out duration-200"
          enterStart="opacity-0"
          enterEnd="opacity-100"
          leave="transition ease-out duration-200"
          leaveStart="opacity-100"
          leaveEnd="opacity-0"
          onClick={() => setUserSidebarOpen(false)}
        />
        
        <div className="fixed inset-y-0 right-0 flex max-w-full pl-10 sm:pl-16">
          <Transition
            show={userSidebarOpen}
            tag="div"
            className="w-screen max-w-md"
            enter="transition ease-in-out duration-500 transform"
            enterStart="translate-x-full"
            enterEnd="translate-x-0"
            leave="transition ease-in-out duration-500 transform"
            leaveStart="translate-x-0"
            leaveEnd="translate-x-full"
          >
            <div className="h-full flex flex-col bg-white dark:bg-slate-800 shadow-xl overflow-y-scroll">
              <div className="px-4 sm:px-6 py-8">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100">
                    {selectedUser ? 'Edit User' : 'Add User'}
                  </h2>
                  <button
                    className="text-slate-400 hover:text-slate-500 dark:text-slate-500 dark:hover:text-slate-400"
                    onClick={() => setUserSidebarOpen(false)}
                  >
                    <span className="sr-only">Close</span>
                    <svg className="w-6 h-6 fill-current" viewBox="0 0 24 24">
                      <path d="M7.95 6.536l4.242-4.243a1 1 0 111.415 1.414L9.364 7.95l4.243 4.242a1 1 0 11-1.415 1.415L7.95 9.364l-4.243 4.243a1 1 0 01-1.414-1.415L6.536 7.95 2.293 3.707a1 1 0 011.414-1.414L7.95 6.536z" />
                    </svg>
                  </button>
                </div>
                
                {errors.general && (
                  <div className="mb-4 px-4 py-2 bg-red-100 dark:bg-red-400/10 text-red-600 dark:text-red-400 rounded">
                    {errors.general}
                  </div>
                )}
                
                <form onSubmit={handleSubmit}>
                  <div className="space-y-4">
                    {/* Avatar upload */}
                    <div>
                      <div className="flex items-center">
                        {formData.avatar ? (
                          <div className="mr-4">
                            <img src={formData.avatar.startsWith('data:') 
                                  ? formData.avatar 
                                  : `data:image/jpeg;base64,${formData.avatar}`
                                } alt="Avatar preview" className="w-16 h-16 rounded-full" />
                          </div>
                        ) : null}
                        <div>
                          <label className="block text-sm font-medium mb-1" htmlFor="avatar">
                            User Avatar
                          </label>
                          <input
                            id="avatar"
                            type="file"
                            className={`form-input w-full px-2 py-1 ${errors.avatar ? 'border-red-300' : 'border-slate-200 dark:border-slate-700'}`}
                            accept="image/*"
                            onChange={handleAvatarChange}
                          />
                          {errors.avatar && (
                            <div className="text-xs mt-1 text-red-500">{errors.avatar}</div>
                          )}
                          <div className="text-xs mt-1 text-slate-500">
                            Large images will be automatically resized to fit within 250KB
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Username */}
                    <div>
                      <label className="block text-sm font-medium mb-1" htmlFor="username">
                        Username <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="username"
                        name="username"
                        className={`form-input w-full ${errors.username ? 'border-red-300' : 'border-slate-200 dark:border-slate-700'}`}
                        type="text"
                        value={formData.username}
                        onChange={handleChange}
                      />
                      {errors.username && (
                        <div className="text-xs mt-1 text-red-500">{errors.username}</div>
                      )}
                    </div>
                    
                    {/* First Name */}
                    <div>
                      <label className="block text-sm font-medium mb-1" htmlFor="first_name">
                        First Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="first_name"
                        name="first_name"
                        className={`form-input w-full ${errors.first_name ? 'border-red-300' : 'border-slate-200 dark:border-slate-700'}`}
                        type="text"
                        value={formData.first_name}
                        onChange={handleChange}
                      />
                      {errors.first_name && (
                        <div className="text-xs mt-1 text-red-500">{errors.first_name}</div>
                      )}
                    </div>
                    
                    {/* Last Name */}
                    <div>
                      <label className="block text-sm font-medium mb-1" htmlFor="last_name">
                        Last Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="last_name"
                        name="last_name"
                        className={`form-input w-full ${errors.last_name ? 'border-red-300' : 'border-slate-200 dark:border-slate-700'}`}
                        type="text"
                        value={formData.last_name}
                        onChange={handleChange}
                      />
                      {errors.last_name && (
                        <div className="text-xs mt-1 text-red-500">{errors.last_name}</div>
                      )}
                    </div>
                    
                    {/* Email */}
                    <div>
                      <label className="block text-sm font-medium mb-1" htmlFor="email">
                        Email <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="email"
                        name="email"
                        className={`form-input w-full ${errors.email ? 'border-red-300' : 'border-slate-200 dark:border-slate-700'}`}
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                      />
                      {errors.email && (
                        <div className="text-xs mt-1 text-red-500">{errors.email}</div>
                      )}
                    </div>
                    
                    {/* Title */}
                    <div>
                      <label className="block text-sm font-medium mb-1" htmlFor="title">
                        Job Title
                      </label>
                      <input
                        id="title"
                        name="title"
                        className="form-input w-full border-slate-200 dark:border-slate-700"
                        type="text"
                        value={formData.title}
                        onChange={handleChange}
                      />
                    </div>
                    
                    {/* Organization */}
                    <div>
                      <label className="block text-sm font-medium mb-1" htmlFor="org">
                        Organization <span className="text-red-500">*</span>
                      </label>
                      <select
                        id="org"
                        name="org"
                        className={`form-select w-full ${errors.org ? 'border-red-300' : 'border-slate-200 dark:border-slate-700'}`}
                        value={formData.org}
                        onChange={handleChange}
                      >
                        <option value="">Select an organization</option>
                        {organizations.map(org => (
                          <option key={org.id} value={org.id}>
                            {org.name}
                          </option>
                        ))}
                      </select>
                      {errors.org && (
                        <div className="text-xs mt-1 text-red-500">{errors.org}</div>
                      )}
                    </div>
                    
                    {/* Country */}
                    <div>
                      <label className="block text-sm font-medium mb-1" htmlFor="country">
                        Country <span className="text-red-500">*</span>
                      </label>
                      <select
                        id="country"
                        name="country"
                        className={`form-select w-full ${errors.country ? 'border-red-300' : 'border-slate-200 dark:border-slate-700'}`}
                        value={formData.country}
                        onChange={handleChange}
                      >
                        <option value="">Select a country</option>
                        {countries.map(country => (
                          <option key={country.code} value={country.code}>
                            {country.name}
                          </option>
                        ))}
                      </select>
                      {errors.country && (
                        <div className="text-xs mt-1 text-red-500">{errors.country}</div>
                      )}
                    </div>
                    
                    {/* City */}
                    <div>
                      <label className="block text-sm font-medium mb-1" htmlFor="city">
                        City
                      </label>
                      <input
                        id="city"
                        name="city"
                        className="form-input w-full border-slate-200 dark:border-slate-700"
                        type="text"
                        value={formData.city}
                        onChange={handleChange}
                      />
                    </div>
                    
                    {/* Office Phone */}
                    <div>
                      <label className="block text-sm font-medium mb-1" htmlFor="office">
                        Office Phone
                      </label>
                      <input
                        id="office"
                        name="office"
                        className="form-input w-full border-slate-200 dark:border-slate-700"
                        type="text"
                        value={formData.office}
                        onChange={handleChange}
                      />
                    </div>
                    
                    {/* Mobile Phone */}
                    <div>
                      <label className="block text-sm font-medium mb-1" htmlFor="mobile">
                        Mobile Phone
                      </label>
                      <input
                        id="mobile"
                        name="mobile"
                        className="form-input w-full border-slate-200 dark:border-slate-700"
                        type="text"
                        value={formData.mobile}
                        onChange={handleChange}
                      />
                    </div>
                    
                    {/* Password */}
                    <div>
                      <label className="block text-sm font-medium mb-1" htmlFor="password">
                        Password {!selectedUser && <span className="text-red-500">*</span>}
                      </label>
                      <input
                        id="password"
                        name="password"
                        className={`form-input w-full ${errors.password ? 'border-red-300' : 'border-slate-200 dark:border-slate-700'}`}
                        type="password"
                        value={formData.password}
                        onChange={handleChange}
                        placeholder={selectedUser ? 'Leave blank to keep current password' : ''}
                      />
                      {errors.password && (
                        <div className="text-xs mt-1 text-red-500">{errors.password}</div>
                      )}
                    </div>
                    
                    {/* Confirm Password */}
                    <div>
                      <label className="block text-sm font-medium mb-1" htmlFor="confirmPassword">
                        Confirm Password {!selectedUser && <span className="text-red-500">*</span>}
                      </label>
                      <input
                        id="confirmPassword"
                        name="confirmPassword"
                        className={`form-input w-full ${errors.confirmPassword ? 'border-red-300' : 'border-slate-200 dark:border-slate-700'}`}
                        type="password"
                        value={formData.confirmPassword}
                        onChange={handleChange}
                      />
                      {errors.confirmPassword && (
                        <div className="text-xs mt-1 text-red-500">{errors.confirmPassword}</div>
                      )}
                    </div>
                    
                    {/* Role */}
                    <div>
                      <label className="block text-sm font-medium mb-1" htmlFor="role">
                        Role
                      </label>
                      <select
                        id="role"
                        name="role"
                        className="form-select w-full border-slate-200 dark:border-slate-700"
                        value={formData.role}
                        onChange={handleChange}
                      >
                        <option value="user">User</option>
                        <option value="admin">Admin</option>
                        <option value="regulator">Regulator</option>
                      </select>
                    </div>
                    
                    {/* Active Status */}
                    <div className="flex items-center">
                      <input
                        id="is_active"
                        name="is_active"
                        type="checkbox"
                        className="form-checkbox"
                        checked={formData.is_active}
                        onChange={handleChange}
                      />
                      <label htmlFor="is_active" className="ml-2 text-sm">
                        Active Account
                      </label>
                    </div>
                  </div>
                  
                  {/* Form buttons */}
                  <div className="flex items-center justify-end mt-6">
                    <button
                      type="button"
                      className="btn-sm border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600 text-slate-600 dark:text-slate-300"
                      onClick={() => setUserSidebarOpen(false)}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn-sm bg-indigo-500 hover:bg-indigo-600 text-white ml-3"
                      disabled={isLoading}
                    >
                      {isLoading ? <LoadingSpinner small /> : selectedUser ? 'Update' : 'Create'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </Transition>
        </div>
      </div>
    </Transition>
  );
}

export default UserSidebar;